import { useCallback, useEffect, useMemo } from 'react';
import type { ChatSocketEvent, SocketNamespace } from '@trinity/chat';
import { LifeCycleSocketEvent } from '@trinity/chat';
import type { Socket } from 'socket.io-client';
import { createLogger } from '../utils';

export const useSetupDefaultSocketListeners = (socket: Socket, namespace: SocketNamespace) => {

  const logger = useMemo(() => createLogger({
    namespace,
  }), [namespace]);

  const logEvents = useCallback((event: ChatSocketEvent, payload: unknown) => logger.event(event, payload), [logger]);
  const handleConnect = useCallback(() => {
    logger.event(LifeCycleSocketEvent.Connect);
  }, [logger, namespace]);
  const handleConnectError = useCallback((err: Error) => {
    logger.event(LifeCycleSocketEvent.ConnectError, err);
  }, [logger, namespace]);
  const handleDisconnect = useCallback((reason: string) => {
    logger.event(LifeCycleSocketEvent.Disconnect);
    logger.log(`[WS Lifecycle] Disconnected from namespace: ${namespace}, Reason: ${reason}`);
  }, [logger, namespace]);

  useEffect(() => {
    if (!socket) return;

    socket.on(LifeCycleSocketEvent.Connect, handleConnect);
    socket.on(LifeCycleSocketEvent.ConnectError, handleConnectError);
    socket.on(LifeCycleSocketEvent.Disconnect, handleDisconnect);

    socket.prependAny(logEvents);

    return () => {
      socket.off(LifeCycleSocketEvent.Connect, handleConnect);
      socket.off(LifeCycleSocketEvent.ConnectError, handleConnectError);
      socket.off(LifeCycleSocketEvent.Disconnect, handleDisconnect);

      socket.offAny(logEvents);
    };
  }, [
    handleConnect,
    handleConnectError,
    handleDisconnect,
    logEvents,
    socket,
  ]);
};